import { useState, useEffect } from "react";
import {
  Calendar,
  Download,
  TrendingUp,
  Package,
  DollarSign,
  TrendingDown,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import { getSalesReport, getSalesTrends } from "../../api/reportsApi";
import { money } from "../../components/utils";
import moment from "moment";

export default function Reports() {
  const [activeTab, setActiveTab] = useState("daily");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const [productsData, setProductsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [trends, setTrends] = useState([]);

  useEffect(() => {
    handleTabChange(tabs?.[0]?.id || "daily");
  }, []);

  const fetchReport = async (params) => {
    try {
      setLoading(true);
      const res = await getSalesReport(params);
      const trends = await getSalesTrends(params);
      setTrends(trends);
      setProductsData(res || []);
    } catch (err) {
      console.error("Failed to fetch reports:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCustom = () => {
    if (!customStartDate || !customEndDate) return;
    fetchReport({
      type: "custom",
      start_date: customStartDate,
      end_date: customEndDate,
    });
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);

    if (tabId === "custom") return;

    const range = getDateRange(tabId);
    if (!range) return;

    setCustomStartDate(range.start_date);
    setCustomEndDate(range.end_date);

    fetchReport({
      type: "custom",
      start_date: range.start_date,
      end_date: range.end_date,
    });
  };
  const getDateRange = (type) => {
    const end = moment().startOf("day"); // today at midnight (local time)
    let start = moment(end);

    switch (type) {
      case "daily":
        start = moment(end);
        break;
      case "weekly":
        start = moment(end).startOf("isoWeek"); // Start of the current week (Monday)
        break;
      case "monthly":
        start = moment(end).startOf("month"); // Start of the current month
        break;
      case "yearly":
        start = moment(end).startOf("year"); // Start of the current year
        break;
      default:
        return null;
    }

    return {
      start_date: start.format("YYYY-MM-DD"),
      end_date: end.format("YYYY-MM-DD"),
    };
  };

  // Summary Calculations
  const totalQuantity = productsData?.totalQuantity || "0.00";
  const totalRevenue = productsData?.totalRevenue || "0.00";

  const tabs = [
    { id: "daily", label: "Daily" },
    { id: "weekly", label: "Weekly" },
    { id: "monthly", label: "Monthly" },
    { id: "yearly", label: "Yearly" },
    { id: "custom", label: "Custom Range" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50">
      <Navbar />

      {/* Header */}
      <div className="bg-white shadow-lg">
        <div className="px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Sales Reports
              </h1>
              <p className="text-gray-600 mt-1">
                Track your product sales performance
              </p>
            </div>
            <button className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors shadow-md">
              <Download className="w-5 h-5" />
              Export Report
            </button>
          </div>
        </div>
      </div>

      {/* MAIN CONTAINER */}
      <div className="px-8 py-8">
        {/* Time Tabs */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="w-6 h-6 text-orange-600" />
            <h2 className="text-xl font-bold text-gray-800">
              Select Time Period
            </h2>
          </div>

          <div className="flex flex-wrap gap-3 mb-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-6 py-2.5 font-semibold rounded-lg transition-all ${
                  activeTab === tab.id
                    ? "bg-orange-600 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Custom Range */}
          {activeTab === "custom" && (
            <div className="flex gap-4 items-center p-4 bg-orange-50 rounded-lg border-2 border-orange-200">
              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <button
                onClick={handleApplyCustom}
                className="mt-7 px-6 py-2 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors"
              >
                Apply
              </button>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Total Quantity */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                Total Products Sold
              </h3>
              <div className="bg-green-100 rounded-lg p-2">
                <Package className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {totalQuantity.toLocaleString()}
            </p>
            <p
              className={`text-sm text-${
                trends?.totalQuantityGrowth < 0 ? "red" : "green"
              }-600 mt-2 flex items-center gap-1`}
            >
              {trends?.totalQuantityGrowth < 0 ? (
                <TrendingDown className="w-4 h-4" />
              ) : (
                <TrendingUp className="w-4 h-4" />
              )}
              <span>{trends?.totalQuantityGrowth} from last period</span>
            </p>
          </div>

          {/* Total Revenue */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                Total Revenue
              </h3>
              <div className="bg-orange-100 rounded-lg p-2">
                <DollarSign className="w-5 h-5 text-orange-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {money(totalRevenue)}
            </p>
            <p
              className={`text-sm text-${
                trends?.totalRevenueGrowth < 0 ? "red" : "green"
              }-600 mt-2 flex items-center gap-1`}
            >
              {trends?.totalRevenueGrowth < 0 ? (
                <TrendingDown className="w-4 h-4" />
              ) : (
                <TrendingUp className="w-4 h-4" />
              )}

              <span>{trends?.totalRevenueGrowth} from last period</span>
            </p>
          </div>

          {/* Product Types */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                Product Types
              </h3>
              <div className="bg-purple-100 rounded-lg p-2">
                <Package className="w-5 h-5 text-purple-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {productsData.length}
            </p>
            <p className="text-sm text-gray-600 mt-2">Active products</p>
          </div>
        </div>

        {/* PRODUCTS TABLE */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              Product Sales Details
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Showing {activeTab} sales data
            </p>
          </div>

          {loading ? (
            <p className="p-6">Loading...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Product Name
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Quantity Sold
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Revenue
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {productsData?.products?.length &&
                    productsData?.products.map((product, index) => (
                      <tr
                        key={product.id}
                        className="hover:bg-orange-50 transition-colors"
                      >
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="bg-gradient-to-br from-orange-500 to-indigo-600 rounded-lg p-2">
                              <Package className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-sm font-semibold">
                              {product.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="text-sm font-bold">
                            {product.quantity?.toLocaleString()}
                          </span>
                          <span className="text-xs text-gray-500 ml-1">
                            units
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="text-sm font-bold text-green-600">
                            {money(product.revenue)}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>

                {/* Footer */}
                <tfoot>
                  <tr className="bg-gray-100 border-t-2 border-gray-300">
                    <td
                      colSpan="2"
                      className="px-6 py-4 font-bold text-gray-900 uppercase"
                    >
                      Total
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-bold">
                        {totalQuantity?.toLocaleString()}
                      </span>
                      <span className="text-xs text-gray-500 ml-1">units</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-semibold text-green-600">
                        {money(totalRevenue)}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
